// AI assisted development
/**
 * Main App Component (T042)
 * 
 * Root component with routing placeholder.
 */

import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import LoadingSpinner from './components/LoadingSpinner';
import RequireAuth from './components/RequireAuth';
import ErrorBoundary from './components/ErrorBoundary';
import FeatureFlaggedRoute from './components/FeatureFlaggedRoute';
import { DEVAGENT_ENABLED_BY_DEFAULT, FeatureFlagNames } from './hooks/useFeatureFlags';
import { lazyWithRetry } from './utils/lazyWithRetry';

// Route-based code splitting (T088)
const EngineerReportCard = lazyWithRetry(() => import('./pages/EngineerReportCard'), 'EngineerReportCard');
const EngineerDirectory = lazyWithRetry(() => import('./pages/EngineerDirectory'), 'EngineerDirectory');
const OrganizationDashboard = lazyWithRetry(() => import('./pages/OrganizationDashboard'), 'OrganizationDashboard');
const PRHistoryDashboard = lazyWithRetry(() => import('./pages/PRHistoryDashboard'), 'PRHistoryDashboard');
const PRDetail = lazyWithRetry(() => import('./pages/PRDetail'), 'PRDetail');
const PRReviewDetail = lazyWithRetry(() => import('./pages/PRReviewDetail'), 'PRReviewDetail');
const JIRAIssuesDashboard = lazyWithRetry(() => import('./pages/JIRAIssuesDashboard'), 'JIRAIssuesDashboard');
const JIRAIssueDetail = lazyWithRetry(() => import('./pages/JIRAIssueDetail'), 'JIRAIssueDetail');
const DefectInvestigation = lazyWithRetry(() => import('./pages/DefectInvestigation'), 'DefectInvestigation');
const FunctionalAreas = lazyWithRetry(() => import('./pages/FunctionalAreas'), 'FunctionalAreas');
const AnalysisDetail = lazyWithRetry(() => import('./pages/AnalysisDetail'), 'AnalysisDetail');
const ReposDashboard = lazyWithRetry(() => import('./pages/ReposDashboard'), 'ReposDashboard');
const RepoDetail = lazyWithRetry(() => import('./pages/RepoDetail'), 'RepoDetail');
const TeamsDashboard = lazyWithRetry(() => import('./pages/TeamsDashboard'), 'TeamsDashboard');
const TeamDetail = lazyWithRetry(() => import('./pages/TeamDetail'), 'TeamDetail');
const FeedbackPage = lazyWithRetry(() => import('./pages/FeedbackPage'), 'FeedbackPage');
const ReleaseNotes = lazyWithRetry(() => import('./pages/ReleaseNotes'), 'ReleaseNotes');
const Admin = lazyWithRetry(() => import('./pages/Admin'), 'Admin');
const ProactiveReview = lazyWithRetry(() => import('./pages/ProactiveReview'), 'ProactiveReview');
const IssueReportersDirectory = lazyWithRetry(() => import('./pages/IssueReportersDirectory'), 'IssueReportersDirectory');
const IssueReporterDetail = lazyWithRetry(() => import('./pages/IssueReporterDetail'), 'IssueReporterDetail');
const IssueReviewsList = lazyWithRetry(() => import('./pages/IssueReviewsList'), 'IssueReviewsList');
const IssueReviewDetail = lazyWithRetry(() => import('./pages/IssueReviewDetail'), 'IssueReviewDetail');
const TechRadar = lazyWithRetry(() => import('./pages/TechRadar'), 'TechRadar');
const Memory = lazyWithRetry(() => import('./pages/Memory'), 'Memory');
const ErrorDetails = lazyWithRetry(() => import('./pages/ErrorDetails'), 'ErrorDetails');

// DevAgent - AI-powered pipeline management
const DevAgentDashboard = lazyWithRetry(() => import('./pages/DevAgentDashboard'), 'DevAgentDashboard');
const DevAgentPipelines = lazyWithRetry(() => import('./pages/DevAgentPipelines'), 'DevAgentPipelines');
const DevAgentPipelineDetail = lazyWithRetry(() => import('./pages/DevAgentPipelineDetail'), 'DevAgentPipelineDetail');
const DevAgentCreatePipeline = lazyWithRetry(() => import('./pages/DevAgentCreatePipeline'), 'DevAgentCreatePipeline');
const DevAgentArtifactViewer = lazyWithRetry(() => import('./pages/DevAgentArtifactViewer'), 'DevAgentArtifactViewer');
const DocumentPackageReviewViewer = lazyWithRetry(() => import('./pages/DocumentPackageReviewViewer'), 'DocumentPackageReviewViewer');
const DevAgentRepositorySelection = lazyWithRetry(() => import('./pages/DevAgentRepositorySelection'), 'DevAgentRepositorySelection');
const DevAgentAnalytics = lazyWithRetry(() => import('./pages/DevAgentAnalytics'), 'DevAgentAnalytics');

function App() {
  return (
    <ErrorBoundary fallbackTitle="Application Error">
      <RequireAuth>
        <Layout>
          <Suspense fallback={<LoadingSpinner message="Loading page..." />}>
            <Routes>
            {/* Default route redirects to DevAgent dashboard (temporary for testing) */}
            {/* <Route path="/" element={<Navigate to="/devagent" replace />} /> */}
            <Route path="/" element={<Navigate to="/organization" replace />} />
            
            {/* Phase 2: Engineer Report Card (MVP) */}
            <Route path="/report-card" element={<EngineerReportCard />} />
            
            {/* Phase 3: Engineer Directory */}
            <Route path="/engineers" element={<EngineerDirectory />} />
            
            {/* Phase 4: Organization Dashboard */}
            <Route path="/organization" element={<OrganizationDashboard />} />
            
            {/* PR History Dashboard (T025) */}
            <Route path="/pr-history" element={<PRHistoryDashboard />} />
            
            {/* PR Detail - view all reviews for a specific PR */}
            {/* NOTE: pr_id is not globally unique across platforms/repositories. Prefer the disambiguated route. */}
            <Route path="/prs/:platform/:repositoryId/:prId" element={<PRDetail />} />
            {/* Backward-compatible route (may be ambiguous across platforms/repos) */}
            <Route path="/prs/:prId" element={<PRDetail />} />
            
            {/* PR Review Detail - view full review from Azure DevOps link */}
            <Route path="/pr/:prId/:reviewId" element={<PRReviewDetail />} />
            
            {/* JIRA Issues Dashboard (Feature 008) */}
            <Route path="/issues" element={<JIRAIssuesDashboard />} />
            <Route path="/issues/:jiraIssueId" element={<JIRAIssueDetail />} />
            
            {/* Proactive Issue Review (Feature 022) - Feature Flagged */}
            <Route 
              path="/issue-review" 
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_ISSUE_QUALITY_V2}>
                  <ProactiveReview />
                </FeatureFlaggedRoute>
              } 
            />
            
            {/* Issue Reporters Directory (Feature 022) - Feature Flagged */}
            <Route 
              path="/issue-reporters" 
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_ISSUE_QUALITY_V2}>
                  <IssueReportersDirectory />
                </FeatureFlaggedRoute>
              } 
            />
            <Route 
              path="/issue-reporters/:reporterId" 
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_ISSUE_QUALITY_V2}>
                  <IssueReporterDetail />
                </FeatureFlaggedRoute>
              } 
            />
            
            {/* Issue Reviews List (Feature 022) - Feature Flagged */}
            <Route 
              path="/issue-reviews" 
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_ISSUE_QUALITY_V2}>
                  <IssueReviewsList />
                </FeatureFlaggedRoute>
              } 
            />
            <Route 
              path="/issue-reviews/:assessmentId" 
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_ISSUE_QUALITY_V2}>
                  <IssueReviewDetail />
                </FeatureFlaggedRoute>
              } 
            />
            
            {/* Defect Investigation (Feature 016) - Feature Flagged */}
            <Route 
              path="/defect-investigation" 
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEFECT_INVESTIGATION}>
                  <DefectInvestigation />
                </FeatureFlaggedRoute>
              } 
            />
            <Route
              path="/functional-areas"
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_FUNCTIONAL_AREA_CATALOG}>
                  <FunctionalAreas />
                </FeatureFlaggedRoute>
              }
            />
            {/* Legacy operator page removed; preparation lives on Functional Areas via Prepare catalog. */}
            <Route path="/functional-area-operations" element={<Navigate to="/functional-areas" replace />} />
            <Route 
              path="/analysis/:analysisId" 
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEFECT_INVESTIGATION}>
                  <AnalysisDetail />
                </FeatureFlaggedRoute>
              } 
            />
            
            {/* Repositories Dashboard */}
            <Route path="/repos" element={<ReposDashboard />} />
            <Route path="/repos/:repositoryName" element={<RepoDetail />} />
            
            {/* Teams Dashboard */}
            <Route path="/teams" element={<TeamsDashboard />} />
            <Route path="/teams/:teamId" element={<TeamDetail />} />
            
            {/* Error Details (Feature: Error Attribution) */}
            <Route path="/errors" element={<ErrorDetails />} />
            
            {/* Feedback and Ideas */}
            <Route path="/feedback" element={<FeedbackPage />} />
            
            {/* Release Notes */}
            <Route path="/release-notes" element={<ReleaseNotes />} />
            
            {/* Admin - Team Management */}
            <Route path="/admin" element={<Admin />} />
            
            {/* DevAgent - fail closed until the feature-flag API explicitly enables it */}
            <Route path="/devagent" element={
              <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEVAGENT} defaultEnabled={DEVAGENT_ENABLED_BY_DEFAULT}>
                <Navigate to="/devagent/dashboard" replace />
              </FeatureFlaggedRoute>
            } />
            <Route path="/devagent/dashboard" element={
              <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEVAGENT} defaultEnabled={DEVAGENT_ENABLED_BY_DEFAULT}>
                <DevAgentDashboard />
              </FeatureFlaggedRoute>
            } />
            <Route path="/devagent/pipelines" element={
              <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEVAGENT} defaultEnabled={DEVAGENT_ENABLED_BY_DEFAULT}>
                <DevAgentPipelines />
              </FeatureFlaggedRoute>
            } />
            <Route path="/devagent/pipelines/:id" element={
              <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEVAGENT} defaultEnabled={DEVAGENT_ENABLED_BY_DEFAULT}>
                <DevAgentPipelineDetail />
              </FeatureFlaggedRoute>
            } />
            <Route path="/devagent/pipelines/:pipelineId/artifacts/:artifactType" element={
              <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEVAGENT} defaultEnabled={DEVAGENT_ENABLED_BY_DEFAULT}>
                <DevAgentArtifactViewer />
              </FeatureFlaggedRoute>
            } />
            <Route path="/devagent/pipelines/:pipelineId/documents/review" element={
              <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEVAGENT} defaultEnabled={DEVAGENT_ENABLED_BY_DEFAULT}>
                <DocumentPackageReviewViewer />
              </FeatureFlaggedRoute>
            } />
            <Route path="/devagent/create" element={
              <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEVAGENT} defaultEnabled={DEVAGENT_ENABLED_BY_DEFAULT}>
                <DevAgentCreatePipeline />
              </FeatureFlaggedRoute>
            } />
            <Route path="/devagent/analytics" element={
              <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEVAGENT} defaultEnabled={DEVAGENT_ENABLED_BY_DEFAULT}>
                <DevAgentAnalytics />
              </FeatureFlaggedRoute>
            } />
            <Route path="/devagent/jira-intake/:token/repository" element={
              <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_DEVAGENT} defaultEnabled={DEVAGENT_ENABLED_BY_DEFAULT}>
                <DevAgentRepositorySelection />
              </FeatureFlaggedRoute>
            } />
            
            {/* Tech Radar (Tech Radar v1) - Feature Flagged */}
            <Route 
              path="/tech-radar" 
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_TECH_RADAR}>
                  <TechRadar />
                </FeatureFlaggedRoute>
              } 
            />
            
            {/* Memory - AI-discovered facts browser - Feature Flagged */}
            <Route 
              path="/memory" 
              element={
                <FeatureFlaggedRoute flagName={FeatureFlagNames.ENABLE_MEMORY}>
                  <Memory />
                </FeatureFlaggedRoute>
              } 
            />
          </Routes>
        </Suspense>
      </Layout>
    </RequireAuth>
    </ErrorBoundary>
  );
}

export default App;
